import { el, escape } from './dom';

export type LimitReason = 'lifetime' | 'file-size' | 'batch-size' | 'manual-pro';

export type LimitModalProps = {
  reason: LimitReason;
  /** For 'file-size' reason, the maximum allowed size in MB. */
  limitMb?: number;
  /** Stripe Checkout URL — defaults to a placeholder. */
  proLink?: string;
  onEnterLicense: () => void;
  onClose: () => void;
};

const STRIPE_DEFAULT = 'https://buy.stripe.com/6oU14mbnl5CRdUe1y06Zy00';

const titleFor = (reason: LimitReason): string => {
  if (reason === 'lifetime') return "You've used your 5 free redactions.";
  if (reason === 'batch-size') return 'Batch processing is a pro feature.';
  if (reason === 'manual-pro') return 'Manual redaction is a pro feature.';
  return 'This file is too big for the free tier.';
};

const bodyFor = (reason: LimitReason, limitMb?: number): string => {
  if (reason === 'lifetime')
    return 'Unlock pro for unlimited redactions, larger files, and manual redaction so you can fix anything the model missed.';
  if (reason === 'batch-size')
    return 'Free covers 3 files at a time. pro removes the cap and bumps the per-file size limit to 100 MB.';
  if (reason === 'manual-pro')
    return "The model misses things sometimes. Pro lets you drag rectangles over anything we missed, or highlight text in PDFs to redact it directly. It's $19 once.";
  return `Files in the free tier max out at ${String(limitMb ?? 10)} MB. pro raises that to 100 MB.`;
};

export const LimitModal = (props: LimitModalProps): HTMLElement => {
  const proHref = props.proLink ?? STRIPE_DEFAULT;
  const root = el(`
    <div class="modal-overlay" id="limit-modal" role="dialog" aria-modal="true">
      <div class="modal">
        <h3 class="modal-title">${escape(titleFor(props.reason))}</h3>
        <p class="modal-body">${escape(bodyFor(props.reason, props.limitMb))}</p>
        <div class="modal-actions">
          <button type="button" class="modal-skip modal-action-spacer" data-action="close">close</button>
          <button type="button" class="modal-skip" data-action="enter-license">enter license key</button>
          <a class="modal-accept" href="${escape(proHref)}" target="_blank" rel="noopener noreferrer">
            get pro for $19 →
          </a>
        </div>
      </div>
    </div>
  `);
  root
    .querySelector<HTMLButtonElement>('[data-action="enter-license"]')
    ?.addEventListener('click', () => {
      root.remove();
      props.onEnterLicense();
    });
  root.querySelector<HTMLButtonElement>('[data-action="close"]')?.addEventListener('click', () => {
    root.remove();
    props.onClose();
  });
  return root;
};
